import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Award, Gift, Mail, PenLine, Send, Sparkles } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { BOOKS } from "@/data/books";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INCLUDES = [
  { icon: PenLine, text: "A personal signed message from Damali inside your book" },
  { icon: Sparkles, text: "Bailey character stickers" },
  { icon: Award, text: "A reading certificate for your little reader" },
  { icon: Mail, text: "A welcome letter to open together" },
];

export default function SignedBooks() {
  const [book, setBook] = useState(BOOKS[0].title);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [giftWrap, setGiftWrap] = useState(false);
  const [sending, setSending] = useState(false);

  const total = (22.95 + (giftWrap ? 2.99 : 0)).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        type: "signed-copy",
        message: `SIGNED COPY ORDER — £${total}\nBook: ${book}\nGift wrap: ${giftWrap ? "YES (+£2.99)" : "No"}\nSign it to / message: ${form.message}`,
      });
      toast.success("Order received — Damali will email you to arrange your signed copy.");
      setForm({ name: "", email: "", message: "" });
      setGiftWrap(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const input = "w-full rounded-xl border border-sand bg-white/80 px-4 py-3 text-sm text-walnut placeholder:text-walnutmuted focus:outline-none focus:ring-2 focus:ring-terracotta/50 focus:border-terracotta transition";

  return (
    <section id="signed" data-testid="signed-books-section" className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <div className="relative">
              <div className="spotlight rounded-[2rem] border border-sand p-6 sm:p-8 overflow-hidden">
                <img
                  src="/images/damali-signing.webp"
                  alt="Damali Allison signing a copy of Bailey's Affirmations at her writing desk"
                  className="w-full aspect-[4/5] object-cover rounded-2xl shadow-[0_30px_60px_-24px_rgba(49,67,92,0.4)]"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-6 -left-3 sm:-left-6 bg-walnut text-cream rounded-2xl px-6 py-4 shadow-xl rotate-[-2deg]">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta mb-1">Direct from the author</p>
                <p className="font-display font-bold text-lg leading-tight">Signed. Stickered.<br />Sealed with love.</p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <Eyebrow className="mb-4">Author-signed copies</Eyebrow>
            <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-walnut mb-5">
              A keepsake, signed by Damali herself
            </h2>
            <p className="text-sm sm:text-base text-walnutsoft leading-relaxed mb-6">
              Order directly from the author and your chosen Bailey book arrives as a
              keepsake — personally signed, with stickers, a reading certificate and a
              welcome letter tucked inside.
            </p>
            <ul className="flex flex-col gap-3 mb-7">
              {INCLUDES.map((item) => (
                <li key={item.text} className="flex items-start gap-3 text-sm sm:text-base text-walnut">
                  <item.icon size={18} className="text-terracotta shrink-0 mt-0.5" /> {item.text}
                </li>
              ))}
            </ul>

            <div className="flex items-end justify-between gap-4 mb-3">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amberbrand">1 · Pick your book</p>
              <div
                data-testid="signed-pack-example"
                className="w-60 sm:w-72 shrink-0 rotate-[3deg] hover:rotate-0 transition-transform duration-500 bg-white border border-sand rounded-xl p-1.5 pb-2 shadow-lg"
              >
                <img
                  src="/images/child-certificate.jpeg"
                  alt="A proud Team Bailey graduate opening her signed copy pack"
                  className="w-full aspect-square object-cover rounded-lg"
                  loading="lazy"
                />
                <p className="pt-1.5 px-0.5 font-mono text-[10px] sm:text-[11px] text-walnutsoft tracking-wide leading-snug">
                  Signed Book pack
                </p>
              </div>
            </div>
            <div data-testid="signed-book-picker" className="flex flex-wrap gap-3 mb-7">
              {BOOKS.map((b) => (
                <motion.button
                  key={b.slug}
                  type="button"
                  data-testid={`signed-pick-${b.slug}`}
                  onClick={() => setBook(b.title)}
                  className={`rounded-xl border-2 p-1.5 transition-colors cursor-pointer ${
                    book === b.title ? "border-terracotta bg-white shadow-md" : "border-sand bg-white/60 hover:border-terracotta/50"
                  }`}
                  whileHover={{ scale: 1.06 }}
                  whileTap={{ scale: 0.94 }}
                  title={b.title}
                >
                  <img src={b.cover} alt={b.title} className="w-12 h-16 object-cover rounded-md" />
                </motion.button>
              ))}
            </div>

            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-amberbrand mb-3">2 · Tell Damali who it's for</p>
            <form data-testid="signed-order-form" onSubmit={submit} className="bg-white/75 border border-sand rounded-3xl p-6 shadow-[0_20px_40px_-24px_rgba(49,67,92,0.3)]">
              <div className="flex flex-col gap-3">
                <input
                  data-testid="signed-name-input"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className={input}
                />
                <input
                  data-testid="signed-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Your email address"
                  className={input}
                />
                <textarea
                  data-testid="signed-message-input"
                  required
                  rows={2}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Who should Damali sign it to? (e.g. To Amara — keep being brave!)"
                  className={`${input} resize-none`}
                />
                <button
                  type="button"
                  data-testid="signed-giftwrap-toggle"
                  onClick={() => setGiftWrap(!giftWrap)}
                  aria-pressed={giftWrap}
                  className={`flex items-center justify-between gap-3 rounded-xl border-2 px-4 py-3 text-left transition-colors cursor-pointer ${
                    giftWrap ? "border-terracotta bg-terracotta/5" : "border-sand bg-white/80 hover:border-terracotta/40"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <Gift size={20} className={giftWrap ? "text-terracotta" : "text-walnutmuted"} />
                    <span>
                      <span className="block text-sm font-display font-bold text-walnut">Add birthday gift wrap</span>
                      <span className="block text-xs text-walnutsoft">Wrapped and ready to gift, straight out of the parcel</span>
                    </span>
                  </span>
                  <span className={`shrink-0 font-display font-bold text-sm ${giftWrap ? "text-terracotta" : "text-walnutsoft"}`}>+£2.99</span>
                </button>
                <button
                  data-testid="signed-order-button"
                  type="submit"
                  disabled={sending}
                  className="group rounded-full bg-terracotta text-cream py-4 font-display font-bold text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-terracottadark transition-colors disabled:opacity-60"
                >
                  {sending ? "Sending…" : `Order your signed copy — £${total}`}
                  <Send size={16} className="group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform" />
                </button>
                <p className="text-xs text-walnutmuted text-center">
                  £22.95 includes your book, signed message, stickers, certificate, welcome letter and postage &amp; packaging. Gift wrap optional at £2.99.
                </p>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
